// @givmo/app-admin — author / manage ONE occasion program (EP-BE8-web).
//
// One dialog, mode driven by the `program` prop:
//   - CREATE (program == null): pick the occasion, the amount, the funding rail, an optional note, and
//     the receipt designee → `POST /admin/company/occasion-programs`.
//   - MANAGE (program set): edit the amount / note / card / designee → `PATCH .../{program_id}`.
//
// ── WHAT THIS DIALOG DELIBERATELY DOES NOT DO ────────────────────────────────────────────────────
//
// 1. NO CHARGE, EVER. A program is a RULE; the money moves later, unattended, on the daily sweep. So
//    there is no confirm-before-charge machinery here and no idempotency-key holder — saving a rule is
//    not a spend. (The attended send/batch dialogs in this same feature DO charge and DO carry that
//    machinery; do not copy it here, and do not remove it there.)
//
// 2. NO OCCASION-TYPE OR FUNDING INPUT IN MANAGE MODE. `PATCH` accepts neither: both change what the
//    program fundamentally IS (funding also decides which money rail and which receipt disposition
//    apply), so switching either is authoring a new program. They render as read-only facts rather than
//    as disabled controls, because a disabled control implies a permission problem rather than a
//    deliberate immutability.
//
// 3. NO "CLEAR CARD" ON A CARD PROGRAM. The backend refuses it — an unattended charge with no card
//    would hold every subsequent occasion — so the card picker offers only real cards and the copy
//    points at pausing, which is what "stop this" actually means.
//
// The save sends ONLY the fields that changed: absent-vs-null is load-bearing server-side, so a
// blanket save would clear the note and the designee as a side effect of an amount edit. That body
// construction lives in `occasion-programs-decode.ts` and is unit-tested there.
//
// THE SIGNED NOTICE AT SETUP (GC-FU12b). Creating a program commits its funding, so the company is
// shown the notice signed for that funding before it does (Jonathan, 2026-11-02, with each notice's
// signature): Notice A on the company wallet, Notice C on a company card (`gift-disclaimer.ts`), in the
// same "Terms" panel as the send dialogs, directly above Create, and following the "Pay with" choice.
// Nothing until a funding source is chosen. Manage mode shows none: its funding was committed at setup
// and cannot change.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  sdk,
  isGivmoError,
  type AdminOut,
  type CompanyOccasionProgramOut,
  type CompanyOccasionType,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyCardsV2Options,
  companiesGetCompanyWalletOptions,
} from '@givmo/api-client/query';
import {
  Button,
  Dialog,
  ErrorMessage,
  Select,
  Skeleton,
  TextField,
  Textarea,
  testIdProp,
  type SelectOption,
} from '@givmo/design-system';
import { adminFullName } from '../admins/admins-decode';
import { cardLabel, extractCards } from '../settings/payment-methods-decode';
import { toWalletFunds } from './gift-cards-decode';
import { GiftDisclosureBlock } from './gift-disclosure-block';
import { companyGiftCardNotice } from './gift-disclaimer';
import { useCompanyGiftCardNotice } from './use-company-gift-card-notice';
import { useEmployerModulePaths } from '../../lib/module-paths';
import {
  buildOccasionProgramCreateBody,
  buildOccasionProgramUpdateBody,
  denominationCentsToInput,
  DUPLICATE_PROGRAM_COPY,
  isOccasionConflictError,
  OCCASION_DENOMINATION_RANGE_LABEL,
  OCCASION_PROGRAM_MESSAGE_MAX,
  occasionFundingLabel,
  occasionTypeDescription,
  occasionTypeLabel,
  parseDenominationCents,
} from './occasion-programs-decode';

const PREFIX = 'gift-card-programs-dialog';
const WALLET_VALUE = 'wallet' as const;
const ADMINS_PAGE_LIMIT = 100;
/**
 * The designee Select's stand-in for "no designee" — a `Select` value must be a string, and this maps
 * to an explicit `null` on the wire (the PATCH clear). Without it there is no way BACK to the
 * Owner-default once a named admin has been chosen, which would make the clear path unreachable from
 * the UI even though the backend supports it.
 */
const DESIGNEE_OWNER_DEFAULT = '__owner-default__';

const OCCASION_OPTIONS: SelectOption[] = [
  { value: 'BIRTHDAY', label: occasionTypeLabel('BIRTHDAY') },
  { value: 'WORK_ANNIVERSARY', label: occasionTypeLabel('WORK_ANNIVERSARY') },
  { value: 'NEW_HIRE', label: occasionTypeLabel('NEW_HIRE') },
];

export interface OccasionProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → create mode; a program → manage mode. */
  program: CompanyOccasionProgramOut | null;
  /** Refresh the program list + preflight after a successful write. */
  onSaved: () => void;
}

export function OccasionProgramDialog({
  open,
  onOpenChange,
  program,
  onSaved,
}: OccasionProgramDialogProps) {
  const isCreate = program === null;
  // Who the receipt falls back to, in this host's words (EP-MNT5(h)).
  const { words } = useEmployerModulePaths();

  const [occasionType, setOccasionType] = useState<CompanyOccasionType>('BIRTHDAY');
  const [amountRaw, setAmountRaw] = useState('');
  const [fundingValue, setFundingValue] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [designee, setDesignee] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState(false);

  const cardsQuery = useQuery(companiesGetCompanyCardsV2Options());
  const cards = useMemo(() => extractCards(cardsQuery.data), [cardsQuery.data]);
  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  const wallet = useMemo(() => toWalletFunds(walletQuery.data), [walletQuery.data]);
  const meQuery = useQuery(companiesGetCompanyAdminOptions());

  // The designee candidates are the company's admins — the same set the backend re-resolves against at
  // spend time. One page is plenty for a picker; an admin team past it can still be reached by leaving
  // the default (which falls back to an Owner).
  const adminsQuery = useQuery({
    queryKey: ['companies-get_company_admins', 'occasion-designee'],
    queryFn: async ({ signal }) => {
      const { data } = await sdk.companiesGetCompanyAdmins({
        query: { 'page[limit]': ADMINS_PAGE_LIMIT, 'page[offset]': 0 },
        signal,
        throwOnError: true,
      });
      return extractList<AdminOut>(data);
    },
    enabled: open,
  });
  const admins = adminsQuery.data ?? [];

  // (Re)prime every field whenever the dialog opens or the target program changes. Create starts blank
  // (its defaults are SEEDED below, once the reads resolve); manage pre-fills from the stored rule.
  //
  // ★ Deliberately keyed on `open` + `program` ONLY. Anything async in this dep list — the signed-in
  // admin, the card list — re-runs the effect when it resolves and would wipe an edit already in
  // progress. Defaults that depend on a read therefore belong in the seed effects below, which only
  // fill a still-empty field and so can never overwrite a choice.
  useEffect(() => {
    if (!open) return;
    setServerError(null);
    setDuplicate(false);
    if (program === null) {
      setOccasionType('BIRTHDAY');
      setAmountRaw('');
      setFundingValue(null);
      setMessage('');
      setDesignee(null);
    } else {
      setOccasionType(program.occasion_type);
      setAmountRaw(denominationCentsToInput(program.denomination_cents));
      setFundingValue(
        program.card_id ?? (program.funding_source === 'company_card' ? null : WALLET_VALUE),
      );
      setMessage(program.recipient_message ?? '');
      setDesignee(program.receipt_designee_user_id ?? null);
    }
  }, [open, program]);

  // Seed the CREATE funding default once cards/wallet resolve: server-default card, else first card,
  // else the wallet. Only seeds while nothing is selected, so it never overrides a choice.
  useEffect(() => {
    if (!open || !isCreate || fundingValue !== null) return;
    const preferred = cards.find((c) => c.isDefault) ?? cards[0];
    if (preferred) setFundingValue(preferred.id);
    else if (walletQuery.isSuccess) setFundingValue(WALLET_VALUE);
  }, [open, isCreate, fundingValue, cards, walletQuery.isSuccess]);

  // Seed the CREATE receipt designee to the signed-in admin once that read resolves. Same
  // only-when-empty guard: a designee the admin has already picked is never replaced.
  useEffect(() => {
    if (!open || !isCreate || designee !== null) return;
    const meId = meQuery.data?.id;
    if (meId !== undefined) setDesignee(meId);
  }, [open, isCreate, designee, meQuery.data?.id]);

  const denominationCents = parseDenominationCents(amountRaw);
  const amountTouched = amountRaw.trim().length > 0;
  const amountError = amountTouched && denominationCents === null;

  // The notice for the funding being committed (CREATE only): A on the wallet, C on a card.
  const setupFundingKind: 'wallet' | 'card' | null =
    !isCreate || fundingValue === null ? null : fundingValue === WALLET_VALUE ? 'wallet' : 'card';
  const setupNotice = companyGiftCardNotice({ kind: setupFundingKind ?? 'wallet' });
  const shownSetupNotice = useCompanyGiftCardNotice(setupNotice, open && setupFundingKind !== null);

  const fundingOptions: SelectOption[] = useMemo(
    () => [
      ...cards.map((c) => ({ value: c.id, label: cardLabel(c) })),
      { value: WALLET_VALUE, label: `Company wallet (${wallet.display})` },
    ],
    [cards, wallet.display],
  );

  // MANAGE mode's card picker: real cards only. A card program's card can be swapped but never cleared
  // (pausing is the way to stop it), so there is no "none" option to pick.
  const cardOptions: SelectOption[] = useMemo(
    () => cards.map((c) => ({ value: c.id, label: cardLabel(c) })),
    [cards],
  );

  const designeeOptions: SelectOption[] = useMemo(
    () => [
      { value: DESIGNEE_OWNER_DEFAULT, label: words.receiptFallbackOption },
      ...admins.map((a) => ({ value: a.id, label: adminFullName(a) })),
    ],
    [admins, words],
  );

  const create = useMutation({
    mutationFn: () => {
      const funding =
        fundingValue === WALLET_VALUE || fundingValue === null
          ? ({ kind: 'wallet' } as const)
          : ({ kind: 'card', cardId: fundingValue } as const);
      return sdk.companiesCreateCompanyOccasionProgram({
        body: buildOccasionProgramCreateBody({
          occasionType,
          // Guarded by `canSave` — the button is inert until the amount parses in range.
          denominationCents: denominationCents ?? 0,
          funding,
          recipientMessage: message,
          receiptDesigneeUserId: designee,
        }),
        throwOnError: true,
      });
    },
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
    },
    onError: (error: unknown) => handleWriteError(error),
  });

  const update = useMutation({
    mutationFn: async () => {
      if (program === null) return;
      const body = buildOccasionProgramUpdateBody(program, {
        denominationCents: denominationCents ?? program.denomination_cents,
        recipientMessage: message,
        // `null` reads as "leave the card alone" — a wallet program has no card control at all, and a
        // card program's selection is always a real card.
        cardId: program.funding_source === 'company_card' ? fundingValue : null,
        receiptDesigneeUserId: designee,
      });
      // Nothing changed → send no request at all rather than an empty PATCH.
      if (body === null) return;
      await sdk.companiesUpdateCompanyOccasionProgram({
        path: { program_id: program.id },
        body,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
    },
    onError: (error: unknown) => handleWriteError(error),
  });

  function handleWriteError(error: unknown) {
    if (isOccasionConflictError(error)) {
      setDuplicate(true);
      setServerError(error.message);
      return;
    }
    setDuplicate(false);
    setServerError(
      isGivmoError(error) ? error.message : 'We couldn’t save this program. Please try again.',
    );
  }

  const submitting = create.isPending || update.isPending;
  const fundingSettled = !cardsQuery.isPending && !walletQuery.isPending;
  /**
   * ★ CREATE requires an EXPLICIT funding selection, not merely a settled read.
   *
   * "Either read succeeded" is not enough: a cards read that succeeds with ZERO cards while the wallet
   * read FAILS leaves the seed effect with nothing to pick, so `fundingValue` stays null — and null
   * maps to the wallet on submit. That would author a wallet-funded program the admin never chose, on
   * the one spend path with no human present when it charges. Requiring the selection makes the
   * unresolvable case inert instead of silently defaulting.
   */
  const canSave =
    denominationCents !== null && !submitting && (isCreate ? fundingValue !== null : true);

  const submit = () => {
    setServerError(null);
    setDuplicate(false);
    if (isCreate) create.mutate();
    else update.mutate();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label={isCreate ? 'Create an occasion program' : 'Manage occasion program'}
        className="flex flex-col gap-16"
        testId={PREFIX}
      >
        <Dialog.Title className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isCreate ? 'New occasion program' : 'Manage program'}
        </Dialog.Title>

        {/* The form scrolls between the fixed title and the fixed foot (the kit's sticky-chrome idiom,
            `dialog.tsx`), so the signed notice and Create below it are on screen together whatever the
            window's height (GC-FU12b). It bleeds into the card's padding and 4px above its top, so a
            field's 4px focus ring is never clipped at the scroll edge. Its last 12px is padding under a
            fade, as the terms panel fades its last visible line: while more of the form is below, the
            line at the edge fades instead of being cut; scrolled to the end, only padding is faded. */}
        <div className="-mx-24 -mt-4 flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto px-24 pt-4 pb-12 [mask-image:linear-gradient(to_bottom,black_calc(100%_-_12px),transparent)]">
          {/* ── The occasion: an input while authoring, a fact once authored ── */}
          {isCreate ? (
            <div className="flex flex-col gap-8">
              <Select
                label="Occasion"
                options={OCCASION_OPTIONS}
                value={occasionType}
                onValueChange={(v) => setOccasionType((v ?? 'BIRTHDAY') as CompanyOccasionType)}
                testId={`${PREFIX}-occasion`}
              />
              <p className="font-sans text-s12 tracking-givmo text-text-tertiary">
                {occasionTypeDescription(occasionType)}
              </p>
            </div>
          ) : (
            <div
              className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp(`${PREFIX}-immutable`)}
            >
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {occasionTypeLabel(program.occasion_type)} ·{' '}
                {occasionFundingLabel(program.funding_source)}
              </span>
              <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
                The occasion and the funding source can’t be changed on an existing program — create
                a new one to change either.
              </span>
            </div>
          )}

          {/* ── Amount ── */}
          <div className="flex flex-col gap-6">
            <TextField
              label="Gift amount (USD)"
              inputMode="numeric"
              placeholder="0"
              value={amountRaw}
              onChange={(e) => setAmountRaw(e.target.value)}
              error={amountError}
              errorMessage={
                amountError
                  ? `Enter a whole-dollar amount from ${OCCASION_DENOMINATION_RANGE_LABEL}.`
                  : undefined
              }
              className="max-w-[220px]"
              testId={`${PREFIX}-amount`}
            />
            <p className="font-sans text-s12 tracking-givmo text-text-tertiary">
              Each gift is worth {OCCASION_DENOMINATION_RANGE_LABEL}, in whole dollars. Changing
              this re-prices future gifts only — gifts already sent keep the amount they were issued
              at.
            </p>
          </div>

          {/* ── Funding: a rail choice while authoring; only the CARD is swappable afterwards ── */}
          {isCreate ? (
            cardsQuery.isPending && walletQuery.isPending ? (
              <Skeleton className="h-[40px] w-full max-w-[360px] rounded-lg" />
            ) : (
              <Select
                label="Pay with"
                options={fundingOptions}
                value={fundingValue}
                onValueChange={setFundingValue}
                className="max-w-[360px]"
                testId={`${PREFIX}-funding`}
              />
            )
          ) : program.funding_source === 'company_card' ? (
            <div className="flex flex-col gap-6">
              <Select
                label="Company card"
                options={cardOptions}
                value={fundingValue}
                onValueChange={setFundingValue}
                className="max-w-[360px]"
                testId={`${PREFIX}-card`}
              />
              <p className="font-sans text-s12 tracking-givmo text-text-tertiary">
                You can switch which card pays, but a card-funded program always needs one. Pause
                the program to stop it.
              </p>
            </div>
          ) : null}

          {isCreate && fundingSettled && fundingValue === null && (
            <p
              className="font-sans text-s13 leading-h15 tracking-givmo text-danger-700"
              {...testIdProp(`${PREFIX}-funding-unavailable`)}
            >
              We couldn’t load a way to pay for these gifts. A program charges with nobody present,
              so it can’t be created until its funding source is known — reload and try again.
            </p>
          )}

          {/* ── Optional note ── */}
          <Textarea
            label="Message (optional)"
            placeholder="Add a note that goes out with every gift…"
            value={message}
            maxLength={OCCASION_PROGRAM_MESSAGE_MAX}
            onChange={(e) => setMessage(e.target.value)}
            helper={`${message.length}/${OCCASION_PROGRAM_MESSAGE_MAX}`}
            className="max-w-[480px]"
            testId={`${PREFIX}-message`}
          />

          {/* ── Receipt designee + the Owner fallback ── */}
          <div className="flex flex-col gap-6">
            <Select
              label="Send tax receipts to"
              options={designeeOptions}
              value={designee ?? DESIGNEE_OWNER_DEFAULT}
              placeholder={adminsQuery.isPending ? 'Loading admins…' : 'Choose an admin'}
              onValueChange={(v) => setDesignee(v === DESIGNEE_OWNER_DEFAULT ? null : v)}
              className="max-w-[360px]"
              testId={`${PREFIX}-designee`}
            />
            <p
              className="font-sans text-s12 leading-h15 tracking-givmo text-text-tertiary"
              {...testIdProp(`${PREFIX}-designee-fallback`)}
            >
              {words.receiptFallbackNote}
            </p>
          </div>
        </div>

        {/* The foot: what answers Create, the notice it commits under, and Create. */}
        <div className="flex shrink-0 flex-col gap-16">
          {/* ── The one-active-program-per-occasion conflict, as a considered state ── */}
          {duplicate && (
            <div
              className="flex flex-col gap-4 rounded-lg border border-warning-100 bg-warning-50 p-12"
              {...testIdProp(`${PREFIX}-duplicate`)}
            >
              <p className="font-sans text-s13 font-w600 leading-h15 tracking-givmo text-warning-700">
                {DUPLICATE_PROGRAM_COPY}
              </p>
            </div>
          )}

          {serverError !== null && !duplicate && (
            <ErrorMessage testId={`${PREFIX}-error`}>{serverError}</ErrorMessage>
          )}

          {/* ── The signed notice for the funding this program commits, directly above Create. ── */}
          {setupFundingKind !== null && (
            <GiftDisclosureBlock notice={setupNotice} shown={shownSetupNotice} />
          )}

          <div className="flex items-center justify-end gap-12">
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
              testId={`${PREFIX}-cancel`}
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              disabled={!canSave}
              loading={submitting}
              onClick={submit}
              testId={`${PREFIX}-submit`}
            >
              {isCreate ? 'Create program' : 'Save changes'}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
